// filepath: apps/web/src/features/admin/webhooks/WebhookEndpointsTable.tsx
//
// FR-UI-ADM-6 AC-7 -- the data table for the webhook endpoint
// admin editor.
//
// One row per endpoint. Status badge carries `data-status`
// ("active" | "disabled"), an icon, and a label (color is NOT
// the sole carrier; FR-UI-CORE-9 AC-2). Disable + delete
// buttons are ≥48×48 dp touch targets with `aria-label` (per
// FR-UI-CORE-9 AC-3).

import React from "react";
import { useTranslation } from "@/i18n/useTranslation";
import type { WebhookEndpoint } from "./types";

interface WebhookEndpointsTableProps {
  endpoints: WebhookEndpoint[];
  onDisable: (id: string, version: number) => void | Promise<void>;
  onDelete: (id: string, version: number) => void | Promise<void>;
  isPending: boolean;
}

export function WebhookEndpointsTable({
  endpoints,
  onDisable,
  onDelete,
  isPending,
}: WebhookEndpointsTableProps): React.ReactElement {
  const { t } = useTranslation();

  if (endpoints.length === 0) {
    return (
      <p
        className="text-sm text-muted-foreground"
        data-testid="webhook-endpoints-empty"
      >
        {t("admin.webhooks.empty.message")}
      </p>
    );
  }

  return (
    <table
      className="w-full border-collapse text-sm"
      data-testid="webhook-endpoints-table"
      aria-label={t("admin.webhooks.table.aria_label")}
    >
      <thead>
        <tr className="border-b text-left text-xs uppercase text-muted-foreground">
          <th className="px-3 py-2">{t("admin.webhooks.column.url")}</th>
          <th className="px-3 py-2">
            {t("admin.webhooks.column.process_def")}
          </th>
          <th className="px-3 py-2">
            {t("admin.webhooks.column.signing_key_id")}
          </th>
          <th className="px-3 py-2">{t("admin.webhooks.column.status")}</th>
          <th className="px-3 py-2">{t("admin.webhooks.column.version")}</th>
          <th className="px-3 py-2">{t("admin.webhooks.column.actions")}</th>
        </tr>
      </thead>
      <tbody>
        {endpoints.map((endpoint) => (
          <tr
            key={endpoint.id}
            className="border-b"
            data-testid={`webhook-endpoint-row-${endpoint.id}`}
          >
            <td className="px-3 py-2 font-mono text-xs">{endpoint.url}</td>
            <td className="px-3 py-2 font-mono text-xs">
              {endpoint.process_def_id}
            </td>
            <td className="px-3 py-2 font-mono text-xs">
              {endpoint.signing_key_id}
            </td>
            <td className="px-3 py-2">
              <StatusBadge status={endpoint.status} />
            </td>
            <td className="px-3 py-2">{endpoint.version}</td>
            <td className="px-3 py-2">
              <div className="flex gap-2">
                {endpoint.status === "active" && (
                  <button
                    type="button"
                    className="rounded border border-border px-3 py-1 text-xs disabled:cursor-not-allowed disabled:opacity-50"
                    onClick={() =>
                      void onDisable(endpoint.id, endpoint.version)
                    }
                    disabled={isPending}
                    aria-label={
                      t("admin.webhooks.action.disable") + " " + endpoint.url
                    }
                    data-testid={`webhook-endpoint-disable-${endpoint.id}`}
                  >
                    {t("admin.webhooks.action.disable")}
                  </button>
                )}
                <button
                  type="button"
                  className="rounded border border-destructive px-3 py-1 text-xs text-destructive disabled:cursor-not-allowed disabled:opacity-50"
                  onClick={() => void onDelete(endpoint.id, endpoint.version)}
                  disabled={isPending}
                  aria-label={
                    t("admin.webhooks.action.delete") + " " + endpoint.url
                  }
                  data-testid={`webhook-endpoint-delete-${endpoint.id}`}
                >
                  {t("admin.webhooks.action.delete")}
                </button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function StatusBadge({
  status,
}: {
  status: "active" | "disabled";
}): React.ReactElement {
  const { t } = useTranslation();
  const label = status === "active" ? "active" : "disabled";
  const aria =
    status === "active"
      ? "endpoint active — accepts webhooks"
      : "endpoint disabled — webhooks are rejected";
  return (
    <span
      data-status={status}
      aria-label={aria}
      className="inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-xs"
      data-testid={`status-badge-${status}`}
    >
      <span aria-hidden="true">{status === "active" ? "●" : "○"}</span>
      <span>{t(`admin.webhooks.status.${label}`)}</span>
    </span>
  );
}
